<script lang="ts">
  /**
   * More - mobile navigation hub behind the fixed "More" tab.
   * Search, sections that aren't tabs, the work queue, and account links.
   * Desktop has the header nav for all of this, so it goes to /dashboard.
   * Contract: plans/mobile-v2.md §3.2. Mockup: docs/mobile/mockups/more-tab.html.
   */
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { Link, router } from '@inertiajs/svelte';
  import { viewport } from '../lib/stores/viewport';
  import MobileTopBar from '../components/mobile/MobileTopBar.svelte';
  import { favoriteViews } from '../lib/stores/favorites';
  import { mobileTabs } from '../lib/stores/mobileTabs';
  import { openGlobalSearch } from '../lib/stores/globalSearch';
  import { openSignOutModal, openWorkQueue, type WorkQueueTab } from '../lib/stores/ui';
  import { getTimelinePath } from '../lib/timelinePath';
  import {
    MOBILE_TAB_LABELS,
    mobileTabHref,
    moreSectionKeys,
    type MobileTabKey,
  } from '../lib/mobileTabs';

  interface Props {
    backlogCount: number;
    priorityTaskCount: number;
    trashCount: number;
  }

  let { backlogCount, priorityTaskCount, trashCount }: Props = $props();

  const SECTION_ICONS: Record<MobileTabKey, { emoji: string; bg: string }> = {
    timeline: { emoji: '📊', bg: '#e0e7ff' },
    projects: { emoji: '📁', bg: '#dbeafe' },
    now: { emoji: '⏱️', bg: '#ffedd5' },
    habits: { emoji: '🌱', bg: '#dcfce7' },
    calendar: { emoji: '📅', bg: '#fee2e2' },
    cycles: { emoji: '🔁', bg: '#ffedd5' },
    boards: { emoji: '🗺️', bg: '#e0f2fe' },
  };

  // Sections whose main editing surface needs a large screen.
  const DESKTOP_FIRST: MobileTabKey[] = ['boards'];

  // Checked once on load only: a phone rotated to landscape is wider than the
  // breakpoint, and redirecting then would throw the user off the page.
  onMount(() => {
    if (!get(viewport).isMobile) router.visit('/dashboard', { replace: true });
  });

  let timelinePath = $derived(getTimelinePath($favoriteViews, { mobile: true }));
  let planSections = $derived(moreSectionKeys($mobileTabs));

  // The work-queue sheet lives on the Timeline page: preselect the tab, then go there.
  function openQueue(tab: WorkQueueTab) {
    openWorkQueue(tab);
    router.visit(timelinePath);
  }
</script>

<svelte:head>
  <title>More · Momentium</title>
</svelte:head>

<div class="more-page">
  <MobileTopBar title="More" showSearch={false}>
    {#snippet below()}
      <button type="button" class="search-field" onclick={openGlobalSearch}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4-4" />
        </svg>
        Search projects and tasks
      </button>
    {/snippet}
  </MobileTopBar>

  {#if planSections.length > 0}
    <section aria-labelledby="more-plan">
      <h2 id="more-plan" class="section-heading">Plan</h2>
      <div class="list">
        {#each planSections as key (key)}
          <Link href={mobileTabHref(key, timelinePath)} class="row">
            <span class="row-icon" style:background={SECTION_ICONS[key].bg} aria-hidden="true">
              {SECTION_ICONS[key].emoji}
            </span>
            <span class="row-main">{MOBILE_TAB_LABELS[key]}</span>
            {#if DESKTOP_FIRST.includes(key)}
              <span class="desktop-badge">Best on desktop</span>
            {/if}
            <span class="chevron" aria-hidden="true">›</span>
          </Link>
        {/each}
      </div>
    </section>
  {/if}

  <section aria-labelledby="more-queue">
    <h2 id="more-queue" class="section-heading">Work queue</h2>
    <div class="list">
      <button type="button" class="row" onclick={() => openQueue('backlog')}>
        <span class="row-icon" style:background="#f3f4f6" aria-hidden="true">📥</span>
        <span class="row-main">Project backlog</span>
        <span class="count">{backlogCount}</span>
        <span class="chevron" aria-hidden="true">›</span>
      </button>
      <button type="button" class="row" onclick={() => openQueue('priority')}>
        <span class="row-icon" style:background="#fef3c7" aria-hidden="true">⭐</span>
        <span class="row-main">Priority tasks</span>
        <span class="count">{priorityTaskCount}</span>
        <span class="chevron" aria-hidden="true">›</span>
      </button>
    </div>
  </section>

  <section aria-labelledby="more-account">
    <h2 id="more-account" class="section-heading">Account</h2>
    <div class="list">
      <Link href="/settings" class="row">
        <span class="row-icon" style:background="#f3f4f6" aria-hidden="true">⚙️</span>
        <span class="row-main">
          Settings
          <span class="row-sub">Timezone, Now, tabs, API keys</span>
        </span>
        <span class="chevron" aria-hidden="true">›</span>
      </Link>
      <Link href="/trash" class="row">
        <span class="row-icon" style:background="#f3f4f6" aria-hidden="true">🗑️</span>
        <span class="row-main">Trash</span>
        {#if trashCount > 0}
          <span class="count">{trashCount}</span>
        {/if}
        <span class="chevron" aria-hidden="true">›</span>
      </Link>
      <button type="button" class="row sign-out" onclick={openSignOutModal}>
        <span class="row-main">Sign out</span>
      </button>
    </div>
  </section>
</div>

<style>
  .more-page {
    min-height: 100%;
    background: #f8fafc;
    padding-bottom: 24px;
  }

  .search-field {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 44px;
    padding: 0 12px;
    border: 0;
    border-radius: 12px;
    background: #eef2f6;
    color: #6b7280;
    font: inherit;
    font-size: 16px;
    text-align: left;
    cursor: pointer;
  }

  .search-field svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
  }

  .section-heading {
    margin: 0;
    padding: 18px 16px 6px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .list {
    margin: 0 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #fff;
  }

  .list :global(.row) {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 52px;
    padding: 8px 14px;
    border: 0;
    border-top: 1px solid #f1f5f9;
    background: none;
    color: #111827;
    font: inherit;
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .list :global(.row:first-child) {
    border-top: 0;
  }

  .list :global(.row:active) {
    background: #f8fafc;
  }

  .row-icon {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 8px;
    font-size: 17px;
  }

  .row-main {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  .row-sub {
    margin-top: 1px;
    font-size: 13px;
    font-weight: 400;
    color: #6b7280;
  }

  .count {
    font-size: 14px;
    color: #6b7280;
  }

  .desktop-badge {
    flex-shrink: 0;
    padding: 2px 6px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
  }

  .chevron {
    color: #cbd5e1;
    font-size: 20px;
    line-height: 1;
  }

  .list .sign-out {
    color: #dc2626;
  }
</style>
